import { computed, Directive, effect, ElementRef, inject, input, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { ResponsiveStyle, ResponsiveStyleInput, StyleService } from '../services/style/style.service';

/** Structural equality for the input map, so callers can pass a fresh object literal on every change detection without touching the DOM. */
function inputsEqual(a: ResponsiveStyleInput | undefined, b: ResponsiveStyleInput | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * Resolves configurable responsive values in CSS instead of JavaScript. Put the configured values on
 * the element that should carry them and the directive writes every relevant breakpoint as a
 * `--gs-r-{property}-{breakpoint}` custom property, plus the `gs-r-{property}` class that reads it
 * (see `responsive-values.css`).
 *
 * This is the server-side-rendering-safe alternative to `StyleService.getCurrentResponsiveValue()`:
 * that one resolves against a signal fed by a `ResizeObserver`, which does not exist on the server,
 * so the server would render the mobile value for every visitor and the client would jump to the real
 * one on hydration. Resolving in CSS makes both renders produce identical markup.
 *
 * The element needs a `gs` query container above it — add `class="gs-responsive-container"` to the
 * region that defines the layout width. Without one only the `default` value applies.
 *
 * @example
 * <footer class="gs-responsive-container" [gsResponsive]="{ padding: config.padding }">
 *   <span [gsResponsive]="{ 'font-size': config.textSize }">…</span>
 * </footer>
 */
@Directive({
  selector: '[gsResponsive]',
  standalone: true
})
export class GsResponsiveDirective {
  // Services
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly renderer = inject(Renderer2);
  private readonly styleService = inject(StyleService);

  // Inputs & Models
  /** The responsive values to resolve, keyed by the CSS property they configure. */
  readonly gsResponsive = input<ResponsiveStyleInput | undefined>(undefined);

  // Properties & Signals
  /** Stable view of the input — content equality, so a new object literal alone does not retrigger. */
  private readonly stableInput = computed(() => this.gsResponsive(), { equal: inputsEqual });
  /** The resolved classes and custom properties; only recomputed when the configured values change. */
  private readonly style = computed<ResponsiveStyle>(() => this.styleService.getResponsiveStyle(this.stableInput()));
  /** Classes applied to the host, tracked for cleanup on change. */
  private appliedClasses: string[] = [];
  /** Custom properties set on the host, tracked for cleanup on change. */
  private appliedVariables: string[] = [];

  constructor() {
    // Rewrites the host: drops the classes and custom properties of the previous run, then applies
    // the ones the current configuration resolves to.
    effect(() => {
      const style = this.style();
      this.clear();
      style.classes.forEach((cls) => {
        this.renderer.addClass(this.element, cls);
        this.appliedClasses.push(cls);
      });
      Object.entries(style.variables).forEach(([name, value]) => {
        this.renderer.setStyle(this.element, name, value, RendererStyleFlags2.DashCase);
        this.appliedVariables.push(name);
      });
    });
  }

  // Methods
  /** Removes the classes and custom properties applied in the previous run. */
  private clear(): void {
    this.appliedClasses.forEach((cls) => this.renderer.removeClass(this.element, cls));
    this.appliedVariables.forEach((name) => this.renderer.removeStyle(this.element, name, RendererStyleFlags2.DashCase));
    this.appliedClasses = [];
    this.appliedVariables = [];
  }
}
